import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { ordersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft,
  Package,
  User,
  Calendar,
  DollarSign,
  RefreshCw,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Copy,
  Mail,
  Phone,
  FileText,
  Save
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const { data: orderData, isLoading, refetch } = useQuery(
    ['order', id],
    () => ordersAPI.getById(id),
    {
      select: (response) => response.data.data.order,
      enabled: !!id
    }
  );

  // Notes state
  const [userNote, setUserNote] = useState('');
  const [resellerNote, setResellerNote] = useState('');
  const [resellerName, setResellerName] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);

  // Fetch notes when order data is available
  useEffect(() => {
    const fetchNotes = async () => {
      if (!orderData?.id) return;
      setNoteLoading(true);
      try {
        const response = await ordersAPI.getNotes(orderData.id);
        if (response.data.success) {
          setUserNote(response.data.data.userNote || '');
          setResellerNote(response.data.data.resellerNote || '');
          setResellerName(response.data.data.resellerName || '');
        }
      } catch (error) {
        console.error('Failed to fetch notes:', error);
      } finally {
        setNoteLoading(false);
      }
    };
    fetchNotes();
  }, [orderData?.id]);

  const handleSaveNote = async () => {
    if (!orderData?.id) return;
    setNoteSaving(true);
    try {
      await ordersAPI.saveNote(orderData.id, userNote);
      toast.success('Note saved successfully');
    } catch (error) {
      toast.error('Failed to save note');
    } finally {
      setNoteSaving(false);
    }
  };

  const handleRetryOrder = async () => {
    if (!isAdmin || !orderData) return;
    
    try {
      await ordersAPI.retry(orderData.id);
      toast.success('Order retry initiated');
      refetch();
    } catch (error) {
      toast.error('Failed to retry order');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      completed: 'badge-success',
      pending: 'badge-warning',
      failed: 'badge-danger',
      processing: 'badge-info'
    };

    return (
      <span className={`badge ${statusClasses[status] || 'badge-secondary'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="text-center py-12">
        <XCircle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Order not found</h3>
        <p className="mt-1 text-sm text-gray-500">
          The order you're looking for doesn't exist or you don't have permission to view it.
        </p>
        <div className="mt-6">
          <Link to="/orders" className="btn btn-primary">
            Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/orders')}
            className="text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">
              Order #{orderData.order_number}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Created on {format(new Date(orderData.created_at + 'Z'), 'MMMM dd, yyyy \'at\' h:mm a')}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {getStatusIcon(orderData.status)}
          {getStatusBadge(orderData.status)}
          {isAdmin && orderData.status === 'failed' && (
            <button
              onClick={handleRetryOrder}
              className="btn btn-outline"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry Order
            </button>
          )}
        </div>
      </div>

      {/* Top Row: 3 Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Order Summary */}
        <div className="card">
          <div className="card-header" style={{ paddingBottom: '0.75rem' }}>
            <h3 className="card-title">Order Summary</h3>
          </div>
          <div className="card-content" style={{ paddingTop: '0.5rem' }}>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Package className="h-5 w-5 text-primary-600" />
                <div className="flex-1">
                  <h4 className="text-base font-medium text-white">{orderData.product_name}</h4>
                  <p className="text-sm text-gray-400">{orderData.variant_name}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-sm text-gray-500">Quantity</p>
                  <p className="text-base font-semibold text-white">{orderData.quantity}</p>
                </div>
                {orderData.total_amount && (
                  <div>
                    <p className="text-sm text-gray-500">Total</p>
                    <p className="text-base font-semibold text-green-600">
                      ${parseFloat(orderData.total_amount).toFixed(2)}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500">Source</p>
                  <p className="text-base font-semibold text-white capitalize">
                    {orderData.payment_method || 'Unknown'}
                  </p>
                </div>
              </div>
              
              {/* Referrer URL */}
              {orderData.referrer_url && (
                <div className="pt-2 border-t border-gray-700">
                  <p className="text-sm text-gray-500 mb-1">Source URL:</p>
                  <a 
                    href={orderData.referrer_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-400 hover:text-blue-300 break-all"
                  >
                    {orderData.referrer_url}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Order Status Timeline */}
        <div className="card">
          <div className="card-header" style={{ paddingBottom: '0.75rem' }}>
            <h3 className="card-title">Order Timeline</h3>
          </div>
          <div className="card-content" style={{ paddingTop: '0.5rem' }}>
            <div className="flow-root">
              <ul className="-mb-8">
                <li>
                  <div className="relative pb-8">
                    <span className="absolute top-4 left-4 -ml-px h-full w-0.5" style={{ backgroundColor: '#2a2a2a' }} />
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center" style={{ boxShadow: '0 0 0 8px var(--primary-bg)' }}>
                          <Calendar className="h-4 w-4 text-white" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5">
                        <div>
                          <p className="text-sm text-white font-medium">Order Created</p>
                          <p className="text-sm text-white">
                            {format(new Date(orderData.created_at + 'Z'), 'MMM dd, yyyy h:mm a')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
                
                {orderData.updated_at !== orderData.created_at && orderData.status !== 'completed' && (
                  <li>
                    <div className="relative pb-8">
                      <div className="relative flex space-x-3">
                        <div>
                          <span className={`h-8 w-8 rounded-full flex items-center justify-center ${
                            orderData.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'
                          }`} style={{ boxShadow: '0 0 0 8px var(--primary-bg)' }}>
                            {getStatusIcon(orderData.status)}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 pt-1.5">
                          <div>
                            <p className="text-sm text-white font-medium">
                              Status: {orderData.status.charAt(0).toUpperCase() + orderData.status.slice(1)}
                            </p>
                            <p className="text-sm text-white">
                              {format(new Date(orderData.updated_at + 'Z'), 'MMM dd, yyyy h:mm a')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                )}
                
                {orderData.status === 'completed' && (
                  <li>
                    <div className="relative">
                      <div className="relative flex space-x-3">
                        <div>
                          <span className="h-8 w-8 rounded-full flex items-center justify-center bg-green-500" style={{ boxShadow: '0 0 0 8px var(--primary-bg)' }}>
                            <CheckCircle className="h-4 w-4 text-white" />
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 pt-1.5">
                          <div>
                            <p className="text-sm text-white font-medium">Order Completed</p>
                            <p className="text-sm text-white">
                              {format(new Date(orderData.updated_at + 'Z'), 'MMM dd, yyyy h:mm a')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Order Details */}
        <div className="card">
          <div className="card-header" style={{ paddingBottom: '0.75rem' }}>
            <h3 className="card-title">Order Details</h3>
          </div>
          <div className="card-content" style={{ paddingTop: '0.5rem' }}>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Order ID:</span>
                <span className="font-medium text-white">{orderData.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Product ID:</span>
                <span className="font-medium text-white">{orderData.product_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Variant ID:</span>
                <span className="font-medium text-white">{orderData.variant_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Created:</span>
                <span className="font-medium text-white">
                  {format(new Date(orderData.created_at + 'Z'), 'MMM dd, yyyy')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Last Updated:</span>
                <span className="font-medium text-white">
                  {format(new Date(orderData.updated_at + 'Z'), 'MMM dd, yyyy')}
                </span>
              </div>
              {orderData.payment_method && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Payment Method:</span>
                  <span className="font-medium text-white capitalize">
                    {orderData.payment_method}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Second Row: 2 Columns - Left: Customer Info + Delivery Details, Right: Deliverables */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Left Column - Customer Information and Delivery Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Information */}
          {(orderData.customer_email || orderData.customer_data) && (
            <div className="card">
              <div className="card-header" style={{ paddingBottom: '0.75rem' }}>
                <h3 className="card-title">Customer Information</h3>
              </div>
              <div className="card-content" style={{ paddingTop: '0.5rem' }}>
                <div className="space-y-3">
                  {/* Show end customer email for API link orders */}
                  {orderData.customer_data?.end_customer_email && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-white">Customer Email:</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-white">
                          {orderData.customer_data.end_customer_email}
                        </span>
                        <button
                          onClick={() => copyToClipboard(orderData.customer_data.end_customer_email)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Show reseller email (order owner) - Admin only */}
                  {isAdmin && orderData.customer_email && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-white">Reseller Email:</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-white">
                          {orderData.customer_email}
                        </span>
                        <button
                          onClick={() => copyToClipboard(orderData.customer_email)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {orderData.customer_data && Object.keys(orderData.customer_data).filter(k => k !== 'end_customer_email').length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Additional Data:</p>
                      <div className="rounded-md p-3" style={{ backgroundColor: '#0a0a0a', border: '1px solid #4a4a4a' }}>
                        {typeof orderData.customer_data === 'object' ? (
                          <div className="space-y-2">
                            {Object.entries(orderData.customer_data)
                              .filter(([key]) => key !== 'end_customer_email')
                              .map(([key, value]) => (
                              <div key={key} className="flex items-center space-x-2">
                                <span className="text-sm text-white capitalize">
                                  {key.replace(/_/g, ' ')}:
                                </span>
                                <span className="text-sm font-medium text-white">
                                  {String(value)}{key === 'discount_applied' ? '%' : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                            {JSON.stringify(orderData.customer_data, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Order Notes */}
          <div className="card" style={{ backgroundColor: '#1a1a1a', border: '1px solid #4a4a4a' }}>
            <div className="card-header" style={{ paddingBottom: '0.75rem' }}>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-purple-500" />
                <h3 className="card-title">Order Notes</h3>
              </div>
            </div>
            <div className="card-content" style={{ paddingTop: '0.5rem' }}>
              {noteLoading ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Admin can see reseller's note */}
                  {isAdmin && (
                    <div style={{ backgroundColor: '#0a0a0a', border: '1px solid #4a4a4a', borderRadius: '8px', padding: '16px' }}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm text-gray-400">Note from Reseller</span>
                        {resellerName && (
                          <span className="text-xs text-purple-400">({resellerName})</span>
                        )}
                      </div>
                      {resellerNote ? (
                        <p className="text-white text-sm whitespace-pre-wrap">{resellerNote}</p>
                      ) : (
                        <p className="text-gray-500 text-sm italic">No note entered</p>
                      )}
                    </div>
                  )}

                  {/* User's own note input */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      {isAdmin ? 'Admin Note' : 'Your Note'}
                    </label>
                    <textarea
                      value={userNote}
                      onChange={(e) => setUserNote(e.target.value)}
                      placeholder="Add a note for this order..."
                      rows={4}
                      style={{
                        width: '100%',
                        backgroundColor: '#0a0a0a',
                        border: '1px solid #4a4a4a',
                        borderRadius: '8px',
                        padding: '12px',
                        color: '#ffffff',
                        fontSize: '14px',
                        resize: 'none',
                        outline: 'none',
                        transition: 'box-shadow 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px #3b198e'}
                      onBlur={(e) => e.target.style.boxShadow = 'none'}
                    />
                    <div className="flex justify-end mt-3">
                      <button
                        onClick={handleSaveNote}
                        disabled={noteSaving}
                        style={{
                          backgroundColor: '#3b198e',
                          color: '#ffffff',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: noteSaving ? 'not-allowed' : 'pointer',
                          opacity: noteSaving ? 0.7 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          fontWeight: '500',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => !noteSaving && (e.currentTarget.style.backgroundColor = '#4a22a8')}
                        onMouseLeave={(e) => !noteSaving && (e.currentTarget.style.backgroundColor = '#3b198e')}
                      >
                        <Save className="h-4 w-4" />
                        {noteSaving ? 'Saving...' : 'Save Note'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Deliverables */}
        <div className="card">
          <div className="card-header" style={{ paddingBottom: '0.75rem' }}>
            <h3 className="card-title">Deliverables</h3>
          </div>
          <div className="card-content" style={{ maxHeight: '600px', overflowY: 'overlay', paddingTop: '0.5rem' }}>
            <div className="variants-scrollbar" style={{ height: '100%' }}>
            {(() => {
              // Collect all keys from order items
              const allKeys = [];
              
              // First check if there are items in order_items table
              if (orderData.items && orderData.items.length > 0) {
                orderData.items.forEach(item => {
                  let response = item.api_response;
                  if (typeof response === 'string') {
                    try {
                      response = JSON.parse(response);
                    } catch (e) {
                      response = null;
                    }
                  }
                  
                  if (response) {
                    if (response.keys && Array.isArray(response.keys)) {
                      response.keys.forEach(key => {
                        allKeys.push({
                          key: key,
                          product: item.product_name,
                          variant: item.variant_name
                        });
                      });
                    } else if (response.key) {
                      allKeys.push({
                        key: response.key,
                        product: item.product_name,
                        variant: item.variant_name
                      });
                    }
                  }
                });
              }
              // Only check main api_response if no items exist (legacy orders)
              else if (orderData.api_response) {
                let response = orderData.api_response;
                if (typeof response === 'string') {
                  try {
                    response = JSON.parse(response);
                  } catch (e) {
                    response = null;
                  }
                }
                
                if (response) {
                  // Check for direct keys in response (legacy format)
                  if (response.keys && Array.isArray(response.keys)) {
                    response.keys.forEach(key => {
                      allKeys.push({
                        key: key,
                        product: orderData.product_name,
                        variant: orderData.variant_name
                      });
                    });
                  } else if (response.key) {
                    allKeys.push({
                      key: response.key,
                      product: orderData.product_name,
                      variant: orderData.variant_name
                    });
                  }
                }
              }
              
              return allKeys.length > 0 ? (
                <div className="space-y-2">
                  {allKeys.map((item, index) => (
                    <div key={index} className="rounded-md p-3" style={{ backgroundColor: '#0a0a0a', border: '1px solid #4a4a4a' }}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500 truncate">{item.product}</p>
                          <p className="text-xs text-gray-400 truncate">{item.variant}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between bg-gray-800 rounded p-2">
                        <code className="text-sm text-green-400 break-all">{item.key}</code>
                        <button
                          onClick={() => copyToClipboard(item.key)}
                          className="ml-2 text-gray-400 hover:text-white flex-shrink-0"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500">No deliverables available</p>
                  <p className="text-xs text-gray-600 mt-1">Keys will appear here once the order is completed</p>
                </div>
              );
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
